import { useState, useEffect } from 'react';

const HardwareAccelerationBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);

  useEffect(() => {
    const checkWebGLSupport = () => {
      try {
        const canvas = document.createElement('canvas');
        const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
        
        if (!gl) {
          // WebGL no disponible
          setShowBanner(true);
          return;
        }

        // Detectar si es software renderer
        const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
        if (debugInfo) {
          const renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
          const vendor = gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL);
          
          // Detectar renderizadores de software comunes
          const softwareRenderers = [
            'Google SwiftShader',
            'Microsoft Basic Render Driver',
            'LLVMpipe',
            'Software',
            'llvmpipe'
          ];
          
          const isSoftware = softwareRenderers.some(sw => 
            renderer?.toLowerCase().includes(sw.toLowerCase()) ||
            vendor?.toLowerCase().includes(sw.toLowerCase())
          );

          if (isSoftware) {
            setShowBanner(true);
          }
        }

        // Test de rendimiento básico
        const startTime = performance.now();
        // Intentar renderizar algo simple
        gl.clear(gl.COLOR_BUFFER_BIT);
        const endTime = performance.now();
        
        // Si tarda mucho en renderizar algo simple, probablemente sea software
        if (endTime - startTime > 50) {
          setShowBanner(true);
        }

      } catch (e) {
        // Error al crear contexto WebGL
        setShowBanner(true);
      }
    };

    // Verificar si el usuario ya vio el banner
    const hasSeenBanner = localStorage.getItem('hardwareAccelBannerSeen');
    if (!hasSeenBanner) {
      checkWebGLSupport();
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem('hardwareAccelBannerSeen', 'true');
    setShowBanner(false);
  };

  const handleDismiss = () => {
    setShowBanner(false);
  };

  const getBrowserInstructions = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    
    if (userAgent.includes('chrome')) {
      return {
        name: 'Chrome',
        steps: [
          'Escribí chrome://settings/system en la barra de direcciones',
          'Activá "Usar aceleración de gráficos cuando esté disponible"',
          'Reiniciá el navegador'
        ]
      };
    } else if (userAgent.includes('firefox')) {
      return {
        name: 'Firefox',
        steps: [
          'Escribí about:config en la barra de direcciones',
          'Buscá "layers.acceleration.disabled"',
          'Asegurate de que esté en "false"',
          'Reiniciá Firefox'
        ]
      };
    } else if (userAgent.includes('edge')) {
      return {
        name: 'Edge',
        steps: [
          'Escribí edge://settings/system en la barra de direcciones',
          'Activá "Usar aceleración de gráficos cuando esté disponible"',
          'Reiniciá el navegador'
        ]
      };
    } else if (userAgent.includes('safari')) {
      return {
        name: 'Safari',
        steps: [
          'Preferencias del Sistema → Batería',
          'Desactivá "Optimizar cargas de video" si está activado',
          'O en Preferencias → Avanzado → "Mostrar menú Desarrollo"',
          'Luego menú Desarrollo → "Activar WebGL"'
        ]
      };
    }
    
    return {
      name: 'tu navegador',
      steps: ['Buscá "aceleración de hardware" o "WebGL" en la configuración de tu navegador']
    };
  };

  if (!showBanner) return null;

  const browserInfo = getBrowserInstructions();

  return (
    <div 
      style={{
        position: 'fixed',
        bottom: '20px',
        left: '50%',
        transform: 'translateX(-50%)',
        backgroundColor: '#1a1a1a',
        color: '#ffffff',
        padding: '16px 24px',
        borderRadius: '12px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
        maxWidth: '500px',
        width: '90%',
        zIndex: 9999,
        fontFamily: 'system-ui, -apple-system, sans-serif'
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
        <span style={{ fontSize: '20px' }}>⚡</span>
        <div style={{ flex: 1 }}>
          <h4 style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: 600 }}>
            Mejorá la experiencia
          </h4>
          <p style={{ margin: '0 0 12px 0', fontSize: '14px', lineHeight: 1.5, color: '#cccccc' }}>
            Para ver las animaciones correctamente, te recomendamos habilitar la 
            <strong> aceleración por hardware</strong> en tu navegador.
          </p>
          
          {showInstructions && (
            <div 
              style={{
                backgroundColor: '#2d2d2d',
                padding: '12px',
                borderRadius: '8px',
                marginBottom: '12px',
                fontSize: '13px'
              }}
            >
              <strong style={{ color: '#fff', display: 'block', marginBottom: '8px' }}>
                Cómo habilitar en {browserInfo.name}:
              </strong>
              <ol style={{ margin: 0, paddingLeft: '20px', color: '#ccc' }}>
                {browserInfo.steps.map((step, i) => (
                  <li key={i} style={{ marginBottom: '4px' }}>{step}</li>
                ))}
              </ol>
            </div>
          )}
          
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button
              onClick={() => setShowInstructions(!showInstructions)}
              style={{
                padding: '8px 16px',
                backgroundColor: 'transparent',
                color: '#fff',
                border: '1px solid #444',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: 500
              }}
            >
              {showInstructions ? 'Ocultar instrucciones' : '¿Cómo habilitar?'}
            </button>
            <button
              onClick={handleAccept}
              style={{
                padding: '8px 16px',
                backgroundColor: '#024ad8',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: 500
              }}
            >
              Continuar igual
            </button>
            <button
              onClick={handleDismiss}
              style={{
                padding: '8px',
                backgroundColor: 'transparent',
                color: '#888',
                border: 'none',
                cursor: 'pointer',
                fontSize: '13px',
                marginLeft: 'auto'
              }}
            >
              ✕
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HardwareAccelerationBanner;
